import { payments } from '@lawbee/domain';
import { Alert, Badge, Button, Card, FormError, formatDate, formatMoney, useToast } from '@lawbee/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CircleCheck, Landmark, LockKeyhole, TriangleAlert, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useParam, useSearchParam } from '../../lib/hooks';
import type { Destination, PaymentKind } from '../../lib/types';
import { PublicFrame } from '../../shell/RootPages';
import { DoneBlock, InvalidLink, publicQueryState } from './shared';

export const DESTINATION_INFO: Record<Destination, { label: string; note: string }> = {
  trust: { label: 'Trust account', note: 'Held in trust until fees are earned.' },
  operating: { label: 'Operating account', note: 'Payment for fees and services already provided.' },
};

function kindLabel(kind: string): string {
  return (payments.PAYMENT_KIND_LABELS as Record<string, string>)[kind] ?? kind.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Pay link
// ---------------------------------------------------------------------------

type PayResponse = {
  invoice: {
    number: string;
    description: string;
    amountCents: number;
    paidCents: number;
    status: string;
    kind: PaymentKind | string;
    destination: Destination;
    dueOn?: string | null;
    payerName?: string | null;
  };
  firm: { name: string };
  notice: string;
};

export function PayPage() {
  const token = useParam('token');
  const [redirecting, setRedirecting] = useState(false);
  const q = useQuery({
    queryKey: ['public', 'pay', token],
    queryFn: () => api.get<PayResponse>(`/api/public/pay/${encodeURIComponent(token)}`),
    retry: false,
  });
  const checkout = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/api/public/pay/${encodeURIComponent(token)}/checkout`),
    onSuccess: (res) => {
      setRedirecting(true);
      window.location.assign(res.url);
    },
  });

  const state = publicQueryState(q, {
    loadingLabel: 'Loading invoice',
    notFound: <InvalidLink title="This payment link is not valid" description="It may have been replaced by a newer link. Please contact the firm." />,
  });
  if (state) return state;
  const { invoice, firm, notice } = q.data!;
  const due = Math.max(0, invoice.amountCents - invoice.paidCents);
  const isVoid = invoice.status === 'void' || invoice.status === 'cancelled';
  const isPaid = !isVoid && (invoice.status === 'paid' || due === 0);
  const dest = DESTINATION_INFO[invoice.destination] ?? DESTINATION_INFO.operating;

  return (
    <PublicFrame firmName={firm.name}>
      <div className="space-y-6">
        {isPaid ? (
          <Alert variant="success" title="This invoice is paid">
            Thank you. No further payment is needed.
          </Alert>
        ) : null}
        {isVoid ? (
          <Alert variant="warning" title="This invoice was cancelled">
            The firm cancelled this invoice, so there is nothing to pay. Contact {firm.name} if you have questions.
          </Alert>
        ) : null}

        <Card className="overflow-hidden">
          <div className="space-y-1 p-6 sm:p-8">
            <p className="text-sm text-muted-foreground">{isPaid ? 'Amount paid' : 'Amount due'}</p>
            <p className="figures text-[44px] font-semibold leading-none tracking-tight sm:text-[52px]">{formatMoney(isPaid ? invoice.amountCents : due)}</p>
            <p className="pt-2 text-[15px]">{invoice.description}</p>
          </div>
          <dl className="grid gap-4 border-t bg-muted/30 px-6 py-5 text-sm sm:grid-cols-2 sm:px-8">
            <div>
              <dt className="text-xs text-muted-foreground">Invoice</dt>
              <dd className="figures mt-0.5 font-medium">{invoice.number}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Type</dt>
              <dd className="mt-0.5 font-medium">{kindLabel(invoice.kind)}</dd>
            </div>
            {invoice.paidCents > 0 && !isPaid ? (
              <div>
                <dt className="text-xs text-muted-foreground">Already paid</dt>
                <dd className="figures mt-0.5 font-medium">
                  {formatMoney(invoice.paidCents)} of {formatMoney(invoice.amountCents)}
                </dd>
              </div>
            ) : null}
            {invoice.dueOn ? (
              <div>
                <dt className="text-xs text-muted-foreground">Due</dt>
                <dd className="figures mt-0.5 font-medium">{formatDate(invoice.dueOn)}</dd>
              </div>
            ) : null}
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Paid to</dt>
              <dd className="mt-0.5 flex items-start gap-2">
                {invoice.destination === 'trust' ? (
                  <Landmark className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <Wallet className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span>
                  <span className="font-medium">{dest.label}</span>
                  <span className="block text-muted-foreground">{dest.note}</span>
                </span>
              </dd>
            </div>
          </dl>
        </Card>

        {!isPaid && !isVoid ? (
          <div className="space-y-3">
            <FormError message={checkout.error ? errorMessage(checkout.error) : null} />
            <Button size="lg" className="w-full" loading={checkout.isPending || redirecting} onClick={() => checkout.mutate()}>
              <LockKeyhole /> Pay {formatMoney(due)}
            </Button>
            <p className="text-center text-xs leading-relaxed text-muted-foreground">
              Secure payment by Stripe. Pay by card or Canadian pre-authorized debit. {notice}
            </p>
          </div>
        ) : null}
      </div>
    </PublicFrame>
  );
}

// ---------------------------------------------------------------------------
// Dev checkout simulator
// ---------------------------------------------------------------------------

type DevCheckoutResponse = { invoiceNumber: string; amountCents: number; firmName: string; destination: Destination };

export function DevCheckoutPage() {
  const sessionId = useParam('sessionId');
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['public', 'dev-checkout', sessionId],
    queryFn: () => api.get<DevCheckoutResponse>(`/api/public/dev-checkout/${encodeURIComponent(sessionId)}`),
    retry: false,
  });
  const complete = useMutation({
    mutationFn: () => api.post<{ ok: boolean; returnUrl: string | null }>(`/api/public/dev-checkout/${encodeURIComponent(sessionId)}/complete`),
    onSuccess: async (res) => {
      const url = res.returnUrl ?? '';
      if ((url.startsWith('/') && !url.startsWith('//')) || /^https?:\/\//.test(url)) window.location.assign(url);
      else await navigate({ to: '/pay/success' });
    },
    onError: (err) => toast.error('Payment failed', errorMessage(err)),
  });

  const state = publicQueryState(q, {
    loadingLabel: 'Loading checkout',
    notFound: <InvalidLink title="Checkout session not found" description="The simulator is only available in development, and sessions expire." />,
  });
  if (state) return state;
  const data = q.data!;
  const dest = DESTINATION_INFO[data.destination] ?? DESTINATION_INFO.operating;

  return (
    <PublicFrame firmName={data.firmName}>
      <div className="space-y-6">
        <div className="flex gap-3 rounded-xl bg-warning/15 px-4 py-3.5 text-sm" role="alert">
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-accent-foreground" aria-hidden />
          <div>
            <p className="font-semibold">Development payment simulator, no real money</p>
            <p className="mt-0.5 text-muted-foreground">Stripe is not configured. Completing this marks the invoice paid in the local database only.</p>
          </div>
        </div>
        <Card className="space-y-4 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">Invoice</span>
            <span className="figures font-medium">{data.invoiceNumber}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">Destination</span>
            <Badge variant={data.destination === 'trust' ? 'info' : 'default'}>{dest.label}</Badge>
          </div>
          <div className="flex items-end justify-between gap-3 border-t pt-4">
            <span className="text-sm text-muted-foreground">Total</span>
            <span className="figures text-[32px] font-semibold leading-none">{formatMoney(data.amountCents)}</span>
          </div>
        </Card>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button size="lg" loading={complete.isPending} onClick={() => complete.mutate()}>
            Simulate successful payment
          </Button>
        </div>
      </div>
    </PublicFrame>
  );
}

// ---------------------------------------------------------------------------
// Success
// ---------------------------------------------------------------------------

export function PaySuccessPage() {
  const invoice = useSearchParam('invoice');
  return (
    <PublicFrame>
      <DoneBlock icon={<CircleCheck />} title="Payment received">
        <p>Thank you. A receipt will be emailed to you.</p>
        {invoice ? (
          <p className="text-sm">
            Invoice <span className="figures font-medium text-foreground">{invoice}</span>
          </p>
        ) : null}
        <p className="text-sm">You can close this page.</p>
      </DoneBlock>
    </PublicFrame>
  );
}

